/**
 * PSSP LABS — "PSSP Precision" theme
 * before-after.js — <pssp-compare> before/after image comparison element.
 *
 * Used by: sections/before-after-slider.liquid (loads this file with
 * `<script src="{{ 'before-after.js' | asset_url }}" defer="defer"></script>`).
 *
 * Markup contract:
 *   <pssp-compare style="--aspect: 4/3; --pos: 50%;">
 *     <div class="before-after__frame">
 *       .before-after__media--before   (base layer, fully visible)
 *       .before-after__media--after    (top layer, clipped via --pos)
 *       .before-after__divider         (decorative divider line, aria-hidden)
 *       input.before-after__range      (REAL range 0–100 — single source of
 *                                       truth; keyboard is native; the styled
 *                                       thumb is the visible drag handle)
 *     </div>
 *   </pssp-compare>
 *
 * Behavior:
 * - The range input drives everything. JS mirrors its value to the host's
 *   --pos custom property and to aria-valuetext ("NN%").
 * - Pointer drags anywhere on the frame map clientX → value using
 *   setPointerCapture (the input itself is pointer-events: none so touch and
 *   mouse behave identically across browsers). The input keeps focus so
 *   arrow keys work immediately after a drag.
 * - A one-time "nudge" of the divider hints at interactivity when the
 *   element first scrolls into view — IntersectionObserver-lazy, gated by
 *   PSSPTheme.motionOK(), cancelled by any user interaction.
 * - No JS / no support → the section's CSS stacks both images (fallback).
 * - All listeners, observers and animation frames are cleaned up in
 *   disconnectedCallback() (Shopify editor section load/unload safe).
 */

(function () {
  'use strict';

  /** Safe motion gate even if global.js has not executed for some reason. */
  function motionOK() {
    if (window.PSSPTheme && typeof window.PSSPTheme.motionOK === 'function') {
      return window.PSSPTheme.motionOK();
    }
    return !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function clamp(value, min, max) {
    return Math.min(Math.max(value, min), max);
  }

  if (!customElements.get('pssp-compare')) {
    customElements.define(
      'pssp-compare',
      class extends HTMLElement {
        connectedCallback() {
          this._frame = this.querySelector('.before-after__frame');
          this._range = this.querySelector('.before-after__range');
          if (!this._frame || !this._range) return;

          this._dragging = false;
          this._activePointerId = null;
          this._nudgeRaf = null;
          this._nudged = false;
          this._io = null;

          /* Sync the visual state with whatever the input holds (a browser
             may restore a previous value on back/forward navigation). */
          this._apply(this._readValue());
          this.classList.add('is-ready');

          /* --- keyboard / native input --------------------------------- */
          this._onInput = () => {
            this._cancelNudge();
            this._apply(this._readValue());
          };
          this._range.addEventListener('input', this._onInput);

          /* --- pointer drag on the whole frame -------------------------- */
          this._onPointerDown = (event) => {
            if (typeof event.button === 'number' && event.button !== 0) return;
            this._cancelNudge();
            this._dragging = true;
            this._activePointerId = event.pointerId;
            try {
              this._frame.setPointerCapture(event.pointerId);
            } catch (error) {
              /* Pointer already released — drag continues uncaptured. */
            }
            this._frame.classList.add('is-dragging');
            this._setFromPointer(event);
            this._range.focus({ preventScroll: true });
            event.preventDefault();
          };

          this._onPointerMove = (event) => {
            if (!this._dragging || event.pointerId !== this._activePointerId) return;
            this._setFromPointer(event);
          };

          this._onPointerUp = (event) => {
            if (!this._dragging || event.pointerId !== this._activePointerId) return;
            this._dragging = false;
            this._activePointerId = null;
            try {
              this._frame.releasePointerCapture(event.pointerId);
            } catch (error) {
              /* Capture already gone — nothing to release. */
            }
            this._frame.classList.remove('is-dragging');
          };

          this._frame.addEventListener('pointerdown', this._onPointerDown);
          this._frame.addEventListener('pointermove', this._onPointerMove);
          this._frame.addEventListener('pointerup', this._onPointerUp);
          this._frame.addEventListener('pointercancel', this._onPointerUp);

          /* --- lazy intro nudge (IntersectionObserver) ------------------- */
          if ('IntersectionObserver' in window && motionOK()) {
            this._io = new IntersectionObserver(
              (entries) => {
                entries.forEach((entry) => {
                  if (!entry.isIntersecting) return;
                  if (this._io) {
                    this._io.disconnect();
                    this._io = null;
                  }
                  this._nudge();
                });
              },
              { threshold: 0.4 }
            );
            this._io.observe(this);
          }
        }

        disconnectedCallback() {
          if (this._range && this._onInput) {
            this._range.removeEventListener('input', this._onInput);
          }
          if (this._frame) {
            if (this._onPointerDown) this._frame.removeEventListener('pointerdown', this._onPointerDown);
            if (this._onPointerMove) this._frame.removeEventListener('pointermove', this._onPointerMove);
            if (this._onPointerUp) {
              this._frame.removeEventListener('pointerup', this._onPointerUp);
              this._frame.removeEventListener('pointercancel', this._onPointerUp);
            }
          }
          if (this._io) {
            this._io.disconnect();
            this._io = null;
          }
          this._cancelNudge();
          this._onInput = null;
          this._onPointerDown = null;
          this._onPointerMove = null;
          this._onPointerUp = null;
          this._frame = null;
          this._range = null;
        }

        /** Current input value as a finite number (fallback 50). */
        _readValue() {
          const value = this._range.valueAsNumber;
          return Number.isFinite(value) ? clamp(value, 0, 100) : 50;
        }

        /** Map a pointer event's clientX to 0–100 and apply it. */
        _setFromPointer(event) {
          const rect = this._frame.getBoundingClientRect();
          if (rect.width <= 0) return;
          const pct = clamp(((event.clientX - rect.left) / rect.width) * 100, 0, 100);
          this._range.value = String(Math.round(pct));
          /* Drive the divider with the exact float for a fluid drag; the
             input itself snaps to whole steps for the keyboard. */
          this._apply(pct);
        }

        /** Write --pos + aria-valuetext. `value` is 0–100. */
        _apply(value) {
          const pct = clamp(value, 0, 100);
          this.style.setProperty('--pos', pct.toFixed(2) + '%');
          this._range.setAttribute('aria-valuetext', Math.round(pct) + '%');
        }

        /**
         * One-time divider nudge: current → +8 → current, ~900ms total,
         * ease-in-out. Purely decorative; skipped/cancelled on interaction.
         */
        _nudge() {
          if (this._nudged || this._dragging || !motionOK()) return;
          this._nudged = true;

          const start = this._readValue();
          const amplitude = start > 88 ? -8 : 8;
          const duration = 900;
          let startTime = null;

          const tick = (now) => {
            if (startTime === null) startTime = now;
            const t = clamp((now - startTime) / duration, 0, 1);
            /* Sine pulse: 0 → 1 → 0 with smooth ends. */
            const pulse = Math.sin(Math.PI * t);
            const eased = pulse * pulse;
            this._apply(start + amplitude * eased);
            if (t < 1) {
              this._nudgeRaf = window.requestAnimationFrame(tick);
            } else {
              this._nudgeRaf = null;
              this._range.value = String(Math.round(start));
              this._apply(start);
            }
          };

          this._nudgeRaf = window.requestAnimationFrame(tick);
        }

        /** Stop the intro nudge and settle on the input's real value. */
        _cancelNudge() {
          this._nudged = true;
          if (this._nudgeRaf !== null && this._nudgeRaf !== undefined) {
            window.cancelAnimationFrame(this._nudgeRaf);
            this._nudgeRaf = null;
            if (this._range) this._apply(this._readValue());
          }
        }
      }
    );
  }
})();
